import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, firstValueFrom } from 'rxjs';
import { StorageService } from './storage.service';
import { environment } from '../../../environments/environment';

export interface UserScreen {
  name: string;
  path: string;
  icon: string | null;
  permissions: string[];
}

export interface UserModule {
  name: string;
  path: string | null;
  icon: string;
  showAsList: boolean;
  screens: UserScreen[];
}

export interface LoginResult {
  code: string;
  name: string;
  firstSurname: string;
  secondSurname: string;
  nickname: string;
  email: string;
  mustChangePassword: boolean;
  canSelfManagePassword: boolean;
  multiEntityAccess: boolean;
  modules: UserModule[];
  permissions: UserModule[];
  entities: any[];
}

export interface ApiResponse<T> {
  code: number;
  success: boolean;
  message: string;
  result: T;
}

export interface CurrentUser {
  name: string;
  firstSurname: string;
  email: string;
  nickname: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http   = inject(HttpClient);
  private router = inject(Router);
  private storage = inject(StorageService);
  private readonly apiUrl   = environment.apiUrl;
  private readonly CSRF_KEY = 'x-csrf-token';

  isAuthenticated = signal<boolean>(!!this.storage.loadStorageData('user'));
  currentUser     = signal<CurrentUser | null>(this.storage.loadStorageData('user') ?? null);

  // ── Login ────────────────────────────────────────────────────────
  login(email: string, password: string): Observable<ApiResponse<LoginResult>> {
    return this.http.post<ApiResponse<LoginResult>>(
      `${this.apiUrl}/public/v1/Auth/SignIn`,
      { Email: email, Password: password, Device: this.getDevice(), Platform: 'CRM' },
      { withCredentials: true },
    ).pipe(
      tap(async (res) => {
        if (res.success && res.result) {
          const r    = res.result;
          const user: CurrentUser = { name: r.name, firstSurname: r.firstSurname, email: r.email, nickname: r.nickname };
          this.storage.saveStorageData('user', user);
          this.storage.saveStorageData('modules', r.permissions ?? r.modules);
          this.isAuthenticated.set(true);
          this.currentUser.set(user);
          await this.fetchCsrfToken();
        }
      }),
    );
  }

  // ── CSRF ─────────────────────────────────────────────────────────
  async fetchCsrfToken(): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.http.get(`${this.apiUrl}/auth/v1/Csrf/Token`, {
          withCredentials: true,
          observe: 'response',
          responseType: 'text',
        }),
      );
      const token = res.headers.get('x-csrf-token');
      if (token) this.saveCsrf(token);
    } catch { /* silently ignored */ }
  }

  getCsrfToken(): string | null {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage.getItem(this.CSRF_KEY);
  }

  private saveCsrf(token: string): void {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(this.CSRF_KEY, token);
  }

  // ── SSO ──────────────────────────────────────────────────────────
  ssoSignIn(): Observable<ApiResponse<{ url: string }>> {
    return this.http.post<ApiResponse<{ url: string }>>(
      `${this.apiUrl}/public/v1/auth/ssosignin`,
      { device: this.getDevice(), platform: 'CRM' },
      { withCredentials: true },
    );
  }

  // Llama al perfil (que en SSO trae permisos) y guarda la sesión igual que login()
  async initSsoSession(): Promise<void> {
    // CSRF con propagación de error: si da 401 el flujo se corta aquí y no llama al profile
    const csrfRes = await firstValueFrom(
      this.http.get(`${this.apiUrl}/auth/v1/Csrf/Token`, {
        withCredentials: true,
        observe: 'response',
        responseType: 'text',
      }),
    );
    const token = csrfRes.headers.get('x-csrf-token');
    if (token) this.saveCsrf(token);

    const res = await firstValueFrom(
      this.http.get<ApiResponse<LoginResult>>(
        `${this.apiUrl}/auth/v1/User/Profile`,
        { withCredentials: true },
      ),
    );
    if (res.success && res.result) {
      const r = res.result;
      const user: CurrentUser = { name: r.name, firstSurname: r.firstSurname, email: r.email, nickname: r.nickname };
      this.storage.saveStorageData('user', user);
      this.storage.saveStorageData('modules', r.permissions ?? r.modules);
      this.isAuthenticated.set(true);
      this.currentUser.set(user);
    }
  }

  // ── Session validation ───────────────────────────────────────────
  validateSession(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.apiUrl}/auth/v1/User/Profile`, { withCredentials: true });
  }

  // ── Logout ───────────────────────────────────────────────────────
  logout(): void {
    if (!this.isAuthenticated()) return;
    // Primero la request: el interceptor aún tiene el CSRF token disponible
    this.http.post(`${this.apiUrl}/auth/v1/Auth/SignOut`, {}, { withCredentials: true })
      .subscribe({ error: () => {} });
    // Luego limpia la sesión local y redirige
    this.clearSession();
    this.router.navigate(['/auth/login']);
  }

  clearSession(): void {
    this.storage.removeStorageData('user');
    this.storage.removeStorageData('modules');
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(this.CSRF_KEY);
    this.isAuthenticated.set(false);
    this.currentUser.set(null);
  }

  // ── Helpers ──────────────────────────────────────────────────────
  getModules(): UserModule[] {
    return this.storage.loadStorageData('modules') ?? [];
  }

  hasPermission(screenPath: string, permission: string): boolean {
    for (const mod of this.getModules()) {
      for (const screen of mod.screens) {
        if (screen.path === screenPath) {
          return screen.permissions.includes(permission);
        }
      }
    }
    return false;
  }

  hasValidToken(): boolean {
    return !!this.storage.loadStorageData('user');
  }

  getDevice(): string {
    return typeof navigator !== 'undefined' ? navigator.userAgent.substring(0, 120) : 'CRM-WEB';
  }
}
